import {AdminPanel} from "@/components/layouts/main/AdminPanel";
import {UserPanel} from "@/components/layouts/main/UserPanel";
import {NotificationInfo} from "@/components/ui/general/NotificationInfo";
import {HomeShell} from "@/pages/common/Home";
import {Cases} from "@/pages/common/Home/Cases";
import {Dissection} from "@/pages/common/Home/Dissection";
import {Article} from "@/pages/common/Home/Dissection/Article";
import {Effectiveness} from "@/pages/common/Home/Effectiveness";
import {Features} from "@/pages/common/Home/Features";
import {Login} from "@/pages/common/Home/Login";
import {Overview} from "@/pages/common/Home/Overview";
import {Suit} from "@/pages/common/Home/Suit";
import {checkAuth} from "@/redux/slices/commonSlices/candidateSlice";
import {
  getNotification,
  setIsAbbott,
  setIsAkvion,
  setIsJnj,
  setIsMayoly,
  setIsMobile,
  setIsNizhpharm,
  setIsValenta,
  setLanguage,
  setNavigateHistory,
  setWidth,
} from "@/redux/slices/commonSlices/settingsSlice";

import {Suspense, useEffect, useState} from "react";

import {
  MARKETING_ROUTE_PATHS,
  isMarketingSitePath,
} from "@helpers/marketingSitePath";
import {
  authenticatedRoutes,
  createRoutes,
  unauthenticatedRoutes,
} from "@routers/lazyRoutes.jsx";
import "@styles/app.scss";
import {BgLinear} from "@ui/general/BgLinear";
import {Notification} from "@ui/general/Notification";
import {Spinner} from "@ui/general/Spinner";
import {H3} from "@ui/titles/H3";
import Cookies from "js-cookie";
import momentTimezone from "moment-timezone";
import "moment/locale/ru";
import moment from "moment/min/moment-with-locales";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {Navigate, Route, Routes, useLocation} from "react-router-dom";

const AuthenticatedMarketingGate = ({navigateHistory, role}) => {
  if (role === "Representative") {
    return navigateHistory && !isMarketingSitePath(navigateHistory) ? (
      <Navigate to={navigateHistory} />
    ) : (
      <Navigate to={"/simulations/call"} />
    );
  }

  return navigateHistory && !isMarketingSitePath(navigateHistory) ? (
    <Navigate to={navigateHistory} />
  ) : (
    <Navigate to={"/admin-panel/dashboard"} />
  );
};

const App = () => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const {feedbackStatus} = useSelector((state) => state.ml.chat);
  const {language, navigateHistory, notification} = useSelector(
    (state) => state.common.settings,
  );
  const {auth, role, isLoading} = useSelector(
    (state) => state.common.candidate,
  );
  const [banner, setBanner] = useState(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const {t} = useTranslation();

  const authRoutes = createRoutes(authenticatedRoutes);
  const unauthRoutes = createRoutes(unauthenticatedRoutes);

  useEffect(() => {
    const handleResize = () => {
      dispatch(setIsMobile(window.innerWidth < 768));
      dispatch(setWidth(window.innerWidth));
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(setIsMayoly(window.location.href.includes("mayoly")));
    dispatch(setIsNizhpharm(window.location.href.includes("nizhpharm")));
    dispatch(setIsAbbott(window.location.href.includes("abbott")));
    dispatch(setIsJnj(window.location.href.includes("jnj")));
    dispatch(setIsValenta(window.location.href.includes("valenta")));
    dispatch(setIsAkvion(window.location.href.includes("akvion")));
  }, [dispatch, location.pathname]);

  useEffect(() => {
    const languageAttribute = document.querySelector("[data-ava-locale]");

    if (languageAttribute) {
      const languageValue = languageAttribute.getAttribute("data-ava-locale");

      Cookies.set("language", languageValue);
      dispatch(setLanguage(languageValue));
    } else {
      Cookies.set("language", "en");
      dispatch(setLanguage("en"));
    }

    dispatch(checkAuth());
  }, []);

  useEffect(() => {
    if (auth) dispatch(getNotification());
  }, [auth]);

  useEffect(() => {
    moment.locale(language);
  }, [language]);

  useEffect(() => {
    if (!navigateHistory) dispatch(setNavigateHistory(location.pathname));
  });

  useEffect(() => {
    if (notification) {
      setBanner({
        date: momentTimezone
          .tz(notification.date, userTimeZone)
          .format("DD.MM.YYYY"),
        time: momentTimezone
          .tz(notification.date, userTimeZone)
          .format("HH:mm"),
      });
    }

    return () => {
      setBanner(null);
    };
  }, [notification]);

  useEffect(() => {
    const rootElement = document.getElementById("root");
    const isChatRoute = location.pathname.startsWith("/chat/");

    if (rootElement) {
      rootElement.classList.toggle("chat-route", isChatRoute);
    }

    return () => {
      if (rootElement) {
        rootElement.classList.remove("chat-route");
      }
    };
  }, [location.pathname]);

  return (
    <Suspense
      fallback={
        <Spinner
          width={80}
          height={80}
        />
      }
    >
      {isLoading ? (
        <BgLinear />
      ) : (
        <>
          {auth && feedbackStatus.state === "completed" && (
            <NotificationInfo duration={60000} />
          )}

          {notification && banner && auth && (
            <Notification>
              <H3>
                <span>
                  {notification.message ||
                    t("notification.message", {
                      date: banner.date,
                      time: banner.time,
                    })}
                </span>
              </H3>
            </Notification>
          )}

          <Routes>
            {auth ? (
              <>
                <Route
                  path={"/"}
                  element={
                    role === "Representative" ? (
                      navigateHistory && navigateHistory !== "/" ? (
                        <Navigate to={navigateHistory} />
                      ) : (
                        <Navigate to={"/simulations/call"} />
                      )
                    ) : navigateHistory && navigateHistory !== "/" ? (
                      <Navigate to={navigateHistory} />
                    ) : (
                      <Navigate to={"/admin-panel/dashboard"} />
                    )
                  }
                />

                {MARKETING_ROUTE_PATHS.map((path) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <AuthenticatedMarketingGate
                        navigateHistory={navigateHistory}
                        role={role}
                      />
                    }
                  />
                ))}

                {authRoutes &&
                  authRoutes.length > 0 &&
                  authRoutes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path}
                      element={route.element}
                    />
                  ))}

                {(role === "Admin" || role === "Manager") && (
                  <Route
                    path={"/admin-panel/*"}
                    element={<AdminPanel />}
                  />
                )}

                {role === "Representative" && (
                  <Route
                    path={"/user-panel/*"}
                    element={<UserPanel />}
                  />
                )}
              </>
            ) : (
              <>
                <Route
                  path={"/"}
                  element={<Navigate to={"/overview"} />}
                />

                <Route element={<HomeShell />}>
                  <Route
                    path={"overview"}
                    element={<Overview />}
                  />
                  <Route
                    path={"features/*"}
                    element={<Features />}
                  />
                  <Route
                    path={"cases"}
                    element={<Cases />}
                  />
                  <Route
                    path={"resources"}
                    element={<Dissection />}
                  />
                  <Route
                    path={"resource/:id/:title"}
                    element={<Article />}
                  />
                  <Route
                    path={"roi"}
                    element={<Effectiveness />}
                  />
                  <Route
                    path={"suit"}
                    element={<Suit />}
                  />
                  <Route
                    path={"login"}
                    element={<Login />}
                  />
                </Route>

                {unauthRoutes &&
                  unauthRoutes.length > 0 &&
                  unauthRoutes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path}
                      element={route.element}
                    />
                  ))}

                <Route
                  path={"/simulations/*"}
                  element={<Navigate to={"/login"} />}
                />
                <Route
                  path={"/admin-panel/*"}
                  element={<Navigate to={"/login"} />}
                />
                <Route
                  path={"/user-panel/*"}
                  element={<Navigate to={"/login"} />}
                />
                <Route
                  path={"/chat/:session/:level/:id"}
                  element={<Navigate to={"/login"} />}
                />
              </>
            )}

            <Route
              path={"*"}
              element={<Navigate to={"/overview"} />}
            />
          </Routes>
        </>
      )}
    </Suspense>
  );
};

export default App;
